import { APIClient } from '../helpers/api_helper';
import axios from 'axios';

const API_BASE_URL = (window as any).process?.env?.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const IS_DEMO_MODE = (window as any).process?.env?.REACT_APP_DEMO_MODE === 'true' || window.location.hostname === 'demo.marttech.io';

class DashboardAPI {
  private apiClient = new APIClient();

  // Check if current user is demo user
  // Demo users are identified by having a demo token (not a real JWT from Keycloak)
  private isDemoUser(): boolean {
    // Check global demo mode flag first
    if (IS_DEMO_MODE) {
      console.log('🔍 isDemoUser: IS_DEMO_MODE is true');
      return true;
    }
    
    const user = sessionStorage.getItem("authUser");
    if (!user) {
      console.log('🔍 isDemoUser: No authUser in sessionStorage');
      return false;
    }
    
    try {
      const userData = JSON.parse(user);
      const token = userData.token || userData.access_token;
      
      console.log('🔍 isDemoUser: Token exists:', !!token, 'Token starts with demo-token-:', token?.startsWith('demo-token-'));
      
      // Demo users have tokens that start with 'demo-token-'
      // Real JWT tokens from Keycloak are much longer and don't start with 'demo-token-'
      // Only return true if token is explicitly a demo token
      if (token && typeof token === 'string' && token.startsWith('demo-token-')) {
        console.log('🔍 isDemoUser: Returning true - demo token detected');
        return true;
      }
      
      // If token exists and is NOT a demo token, it's a real user
      // (Real JWT tokens are long strings that don't start with 'demo-token-')
      if (token && typeof token === 'string' && !token.startsWith('demo-token-')) {
        console.log('🔍 isDemoUser: Returning false - real JWT token detected');
        return false;
      }
      
      // No token or invalid token - not a demo user
      console.log('🔍 isDemoUser: Returning false - no valid token');
      return false;
    } catch (e) {
      console.error('🔍 isDemoUser: Error parsing user data:', e);
      return false;
    }
  }

  // Transactions API
  async getTransactions(params?: {
    page?: number;
    size?: number;
    storeId?: string;
    from?: string;
    to?: string;
  }) {
    if (this.isDemoUser()) {
      // Return mock data for demo
      return this.getMockTransactions(params);
    }

    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());
    if (params?.storeId) queryParams.append('storeId', params.storeId);
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);

    const url = `${API_BASE_URL}/transactions${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response: any = await this.apiClient.get(url, {});
    
    // Backend returns Page<TransactionDTO>
    const pageData = response?.data || response;
    const transactions = pageData?.content || [];
    
    // Map backend TransactionDTO to frontend Transaction format
    const mappedTransactions = transactions.map((tx: any) => this.mapTransactionDTOToTransaction(tx));
    
    return {
      content: mappedTransactions,
      totalElements: pageData?.totalElements || 0,
      totalPages: pageData?.totalPages || 0,
      size: pageData?.size || 20,
      number: pageData?.number || 0
    };
  }

  // Helper function to map backend TransactionDTO to frontend Transaction format
  private mapTransactionDTOToTransaction(txDTO: any): any {
    // Format date
    const date = txDTO.businessDate || txDTO.localDateTime || txDTO.beginDateTime;
    const formattedDate = date ? new Date(date).toLocaleString('tr-TR') : new Date().toLocaleString('tr-TR');
    
    // Map transaction items
    const items = (txDTO.transactionItems || []).map((item: any) => ({
      name: item.itemDescription || '',
      qty: item.itemQuantity || 0,
      unitPrice: item.itemGrossAmount ? parseFloat(item.itemGrossAmount.toString()) : 0,
      total: item.itemGrossAmount ? parseFloat(item.itemGrossAmount.toString()) : 0,
      alerts: item.alerts || [],
      isVoided: item.itemVoid || false
    }));
    
    // Calculate totals
    const totalGross = txDTO.totalGrossAmount ? parseFloat(txDTO.totalGrossAmount.toString()) : 0;
    const totalTax = txDTO.totalTaxAmount ? parseFloat(txDTO.totalTaxAmount.toString()) : 0;
    const total = totalGross + totalTax;
    
    // Count alerts
    const alertCount = items.reduce((sum: number, item: any) => sum + (item.alerts?.length || 0), 0);
    
    // Determine transaction type
    let transactionType = txDTO.transactionType || 'sales';
    if (txDTO.voided) transactionType = 'void';
    if (txDTO.returned) transactionType = 'return';
    if (txDTO.canceled) transactionType = 'void';
    
    // Format payment info
    const tenderType = txDTO.paymentType || txDTO.paymentCode || '';
    const creditCard = tenderType === 'credit_card' ? (txDTO.loyaltyId || '') : '';
    
    return {
      id: txDTO.transactionId || '',
      receiptNumber: txDTO.receiptNumber || '',
      checkout: 1, // Not in DTO, default to 1
      cashier: '', // Not in DTO, will need to be fetched separately or added to DTO
      storeData: {
        id: txDTO.storeId || '',
        name: '', // Will be enriched with store name
        city: '',
        province: '',
        phone: '',
        address: '',
        active: true,
        createdOn: ''
      },
      store: '', // Will be enriched with store name
      date: formattedDate,
      items: items,
      total_gross_amount: `₺${totalGross.toFixed(2)}`,
      total_tax: `₺${totalTax.toFixed(2)}`,
      total: `₺${total.toFixed(2)}`,
      alert_count: alertCount,
      transaction_type: transactionType,
      tender_type: tenderType,
      credit_card: creditCard,
      duration: 0 // Not in DTO
    };
  }

  async getFilteredTransactions(params?: {
    storeId?: string;
    from?: string;
    to?: string;
    page?: number;
    size?: number;
  }) {
    if (this.isDemoUser()) {
      // Return mock data for demo
      return this.getMockFilteredTransactions(params);
    }

    const queryParams = new URLSearchParams();
    if (params?.storeId) queryParams.append('storeId', params.storeId);
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const url = `${API_BASE_URL}/transactions/filter${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response: any = await this.apiClient.get(url, {});
    
    // Backend returns Page<TransactionDTO>
    const pageData = response?.data || response;
    const transactions = pageData?.content || [];
    
    // Map backend TransactionDTO to frontend Transaction format
    const mappedTransactions = transactions.map((tx: any) => this.mapTransactionDTOToTransaction(tx));
    
    return {
      content: mappedTransactions,
      totalElements: pageData?.totalElements || 0,
      totalPages: pageData?.totalPages || 0,
      size: pageData?.size || 20,
      number: pageData?.number || 0
    };
  }

  // Alerts API
  async getAlerts(params?: {
    page?: number;
    size?: number;
    status?: string;
    storeId?: string;
    from?: string;
    to?: string;
  }) {
    if (this.isDemoUser()) {
      // Return mock data for demo
      return this.getMockAlerts(params);
    }

    // Backend returns List<AlertDTO>, not paginated
    // We'll handle pagination on frontend if needed
    const url = `${API_BASE_URL}/alerts`;
    const response: any = await this.apiClient.get(url, {});
    
    // Extract data if it's an AxiosResponse, otherwise use response directly
    const alerts: any[] = Array.isArray(response) ? response : (response?.data || []);
    
    // Apply client-side pagination if needed
    const page = params?.page || 0;
    const size = params?.size || 20;
    const start = page * size;
    const end = start + size;
    
    // Filter by status if provided
    let filteredAlerts = alerts;
    if (params?.status) {
      // Map frontend status to backend status for filtering
      const statusMap: { [key: string]: string } = {
        'new': 'OPEN',
        'in_review': 'IN_REVIEW',
        'closed': 'CLOSED'
      };
      const backendStatus = statusMap[params.status] || params.status;
      filteredAlerts = alerts.filter((alert: any) => alert.status === backendStatus || alert.status === params.status);
    }
    
    // Map backend AlertDTO to frontend Alert format
    const mappedAlerts = filteredAlerts.map((alert: any) => this.mapAlertDTOToAlert(alert));
    
    return {
      content: mappedAlerts.slice(start, end),
      totalElements: mappedAlerts.length,
      totalPages: Math.ceil(mappedAlerts.length / size),
      size: size,
      number: page
    };
  }

  async getAlertById(alertId: string) {
    if (this.isDemoUser()) {
      // Return mock alert for demo
      const { generateFakeTransactionsWithAlerts } = await import('../data/transactions');
      const mockAlerts = generateFakeTransactionsWithAlerts(1500).alerts;
      return mockAlerts.find((a: any) => a.id === alertId) || null;
    }

    // Backend doesn't have getById endpoint, so fetch all and filter
    const url = `${API_BASE_URL}/alerts`;
    const response: any = await this.apiClient.get(url, {});
    const alerts: any[] = Array.isArray(response) ? response : (response?.data || []);
    const alert = alerts.find((a: any) => a.id === alertId);
    return alert ? this.mapAlertDTOToAlert(alert) : null;
  }

  // Helper function to map backend AlertDTO to frontend Alert format
  private mapAlertDTOToAlert(alertDTO: any): Alert {
    // Map backend status (OPEN, IN_REVIEW, CLOSED) to frontend status (new, in_review, closed)
    const statusMap: { [key: string]: AlertStatus } = {
      'OPEN': 'new',
      'IN_REVIEW': 'in_review',
      'CLOSED': 'closed'
    };

    // Use transactionId as receiptNumber for now (can be enhanced later to fetch actual receiptNumber)
    const receiptNumber = alertDTO.transactionId || alertDTO.receiptNumber || '';

    return {
      id: alertDTO.id || '',
      receiptNumber: receiptNumber,
      store: {
        id: alertDTO.storeId || '',
        name: '', // Store name not in AlertDTO, will be enriched later
        city: '',
        province: '',
        phone: '',
        address: '',
        active: true,
        createdOn: ''
      },
      scenarioId: alertDTO.scenarioId || '',
      scenarioName: alertDTO.scenarioName || '',
      ruleId: alertDTO.ruleId || '',
      ruleName: alertDTO.ruleName || '',
      date: alertDTO.createdAt ? new Date(alertDTO.createdAt).toISOString() : new Date().toISOString(),
      status: statusMap[alertDTO.status] || 'new',
      reviewedBy: alertDTO.reviewedByName,
      reviewDate: alertDTO.reviewedAt ? new Date(alertDTO.reviewedAt).toISOString() : undefined,
      videoUrl: undefined // Not in AlertDTO
    };
  }

  async updateAlertStatus(alertId: string, status: string, reviewNotes?: string) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true, message: 'Alert status updated successfully (demo mode)' };
    }

    // Backend uses separate endpoints: /review and /close
    // Map status to correct endpoint
    let url: string;
    if (status === 'IN_REVIEW' || status === 'REVIEW' || status === 'in_review') {
      url = `${API_BASE_URL}/alerts/${alertId}/review`;
      return this.apiClient.create(url, { reviewNotes });
    } else if (status === 'CLOSED' || status === 'CLOSE' || status === 'closed') {
      url = `${API_BASE_URL}/alerts/${alertId}/close`;
      return this.apiClient.create(url, { reviewNotes });
    } else {
      // For other statuses, try the generic endpoint (if it exists)
      url = `${API_BASE_URL}/alerts/${alertId}/status`;
      return this.apiClient.put(url, { status, reviewNotes });
    }
  }

  // Stores API
  async getStores() {
    if (this.isDemoUser()) {
      // Return mock stores for demo
      return this.getMockStores();
    }

    const url = `${API_BASE_URL}/stores`;
    return this.apiClient.get(url, {});
  }

  async getStoreGroups() {
    if (this.isDemoUser()) {
      // Return mock store groups for demo
      return this.getMockStoreGroups();
    }

    const url = `${API_BASE_URL}/store-groups`;
    return this.apiClient.get(url, {});
  }

  async getStoreGroupById(storeGroupId: string) {
    if (this.isDemoUser()) {
      // Return mock store group for demo
      const mockStoreGroups = await this.getMockStoreGroups();
      return Array.isArray(mockStoreGroups) 
        ? mockStoreGroups.find((g: any) => g.id === storeGroupId) || null
        : null;
    }

    const url = `${API_BASE_URL}/store-groups/${storeGroupId}`;
    return this.apiClient.get(url, {});
  }

  async createStoreGroup(storeGroupData: {
    name: string;
    description?: string;
    active?: boolean;
  }) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo store group created:', storeGroupData);
      return { ...storeGroupData, id: `demo-store-group-${Date.now()}` };
    }

    const url = `${API_BASE_URL}/store-groups`;
    return this.apiClient.create(url, storeGroupData);
  }

  async updateStoreGroup(storeGroupId: string, storeGroupData: {
    name?: string;
    description?: string;
    active?: boolean;
  }) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo store group updated:', storeGroupId, storeGroupData);
      return { ...storeGroupData, id: storeGroupId };
    }

    const url = `${API_BASE_URL}/store-groups/${storeGroupId}`;
    return this.apiClient.put(url, storeGroupData);
  }

  async deleteStoreGroup(storeGroupId: string) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo store group deleted:', storeGroupId);
      return { success: true };
    }

    const url = `${API_BASE_URL}/store-groups/${storeGroupId}`;
    return this.apiClient.delete(url, {});
  }

  async assignStoresToStoreGroup(storeGroupId: string, storeIds: string[]) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo stores assigned to store group:', storeGroupId, storeIds);
      return { success: true };
    }

    const url = `${API_BASE_URL}/store-groups/${storeGroupId}/stores`;
    return this.apiClient.put(url, { storeIds });
  }

  async removeStoresFromStoreGroup(storeGroupId: string, storeIds: string[]) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo stores removed from store group:', storeGroupId, storeIds);
      return { success: true };
    }

    const url = `${API_BASE_URL}/store-groups/${storeGroupId}/stores`;
    return this.apiClient.delete(url, { data: { storeIds } });
  }

  async updateStore(storeId: string, storeData: any) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true, message: 'Store updated successfully (demo mode)' };
    }

    const url = `${API_BASE_URL}/stores/${storeId}`;
    // Only send fields that are part of StoreDTO
    const storeBody = {
      name: storeData.name,
      address: storeData.address,
      phone: storeData.phone,
      city: storeData.city,
      province: storeData.province,
      active: storeData.active !== undefined ? storeData.active : true
    };
    return this.apiClient.put(url, storeBody);
  }

  // Scenarios API
  async getScenarios() {
    if (this.isDemoUser()) {
      // Return mock scenarios for demo
      return this.getMockScenarios();
    }

    const url = `${API_BASE_URL}/scenarios`;
    return this.apiClient.get(url, {});
  }

  async getScenarioById(scenarioId: string) {
    if (this.isDemoUser()) {
      // Return mock scenario for demo
      const { mockScenarios } = await import('../data/scenarios');
      return mockScenarios.find((s: any) => s.id === scenarioId) || null;
    }

    const url = `${API_BASE_URL}/scenarios/${scenarioId}`;
    return this.apiClient.get(url, {});
  }

  /**
   * Create Scenario
   * 
   * NOTE: Scenarios are pre-configured by MartTech and cannot be created by customers.
   * MartTech prepares and adds customer-specific scenarios.
   * This method is only available in demo mode for testing purposes.
   */
  async createScenario(scenarioData: any) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true, message: 'Scenario created successfully (demo mode)', id: 'demo-scenario-' + Date.now() };
    }

    // Scenarios are managed by MartTech, not by customers
    throw new Error('Scenarios are pre-configured by MartTech. Please contact your administrator to add new scenarios.');
  }

  /**
   * Update Scenario
   * 
   * NOTE: Scenarios are pre-configured by MartTech and cannot be modified by customers.
   * This method is only available in demo mode for testing purposes.
   */
  async updateScenario(scenarioId: string, scenarioData: any) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true, message: 'Scenario updated successfully (demo mode)' };
    }

    // Scenarios are managed by MartTech, not by customers
    throw new Error('Scenarios are pre-configured by MartTech and cannot be modified. Please contact your administrator.');
  }

  /**
   * Delete Scenario
   * 
   * NOTE: Scenarios are pre-configured by MartTech and cannot be deleted by customers.
   * This method is only available in demo mode for testing purposes.
   */
  async deleteScenario(scenarioId: string) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true, message: 'Scenario deleted successfully (demo mode)' };
    }

    // Scenarios are managed by MartTech, not by customers
    throw new Error('Scenarios are pre-configured by MartTech and cannot be deleted. Please contact your administrator.');
  }

  // Rules API
  async getRules(params?: {
    scenarioId?: string;
    storeId?: string;
    page?: number;
    size?: number;
  }) {
    if (this.isDemoUser()) {
      // Return mock rules for demo
      return this.getMockRules(params);
    }

    const queryParams = new URLSearchParams();
    if (params?.scenarioId) queryParams.append('scenarioId', params.scenarioId);
    if (params?.storeId) queryParams.append('storeId', params.storeId);
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const url = `${API_BASE_URL}/rules${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.apiClient.get(url, {});
  }

  async createRule(ruleData: any) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true, message: 'Rule created successfully (demo mode)', id: 'demo-rule-' + Date.now() };
    }

    // Backend requires scenarioId in path: POST /rules/{scenarioId}
    const scenarioId = ruleData.scenarioId;
    if (!scenarioId) {
      throw new Error('scenarioId is required to create a rule');
    }
    
    // Convert conditionGroup to conditionTreeJson
    const conditionTreeJson = ruleData.conditionGroup 
      ? this.conditionGroupToTreeJson(ruleData.conditionGroup)
      : null;
    
    const url = `${API_BASE_URL}/rules/${scenarioId}`;
    // Remove conditionGroup and scenarioId from body, add conditionTreeJson
    const { scenarioId: _, conditionGroup: __, ...ruleBody } = ruleData;
    const requestBody = {
      ...ruleBody,
      conditionTreeJson: conditionTreeJson
    };
    
    return this.apiClient.create(url, requestBody);
  }

  async updateRule(ruleId: string, ruleData: any) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true, message: 'Rule updated successfully (demo mode)' };
    }

    // Convert conditionGroup to conditionTreeJson
    const conditionTreeJson = ruleData.conditionGroup 
      ? this.conditionGroupToTreeJson(ruleData.conditionGroup)
      : null;
    
    const url = `${API_BASE_URL}/rules/${ruleId}`;
    // Remove conditionGroup from body, add conditionTreeJson
    const { conditionGroup: _, ...ruleBody } = ruleData;
    const requestBody = {
      ...ruleBody,
      conditionTreeJson: conditionTreeJson
    };
    
    return this.apiClient.put(url, requestBody);
  }

  async deleteRule(ruleId: string) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true, message: 'Rule deleted successfully (demo mode)' };
    }

    const url = `${API_BASE_URL}/rules/${ruleId}`;
    return this.apiClient.delete(url, {});
  }

  async getRuleById(ruleId: string) {
    if (this.isDemoUser()) {
      // Return mock rule for demo
      const { mockRules } = await import('../data/rules');
      return mockRules.find((r: any) => r.id === ruleId) || null;
    }

    const url = `${API_BASE_URL}/rules/${ruleId}`;
    return this.apiClient.get(url, {});
  }

  /**
   * Convert frontend conditionGroup to backend conditionTreeJson
   */
  private conditionGroupToTreeJson(conditionGroup: any): string {
    if (!conditionGroup) {
      return JSON.stringify({ type: 'AND', conditions: [] });
    }

    const convertNode = (group: any): any => {
      if (group.field) {
        // It's a condition, not a group
        return {
          type: 'CONDITION',
          condition: {
            field: group.field,
            operator: group.operator,
            value: group.value
          }
        };
      }

      // It's a group
      const nodeType = group.join?.toUpperCase() === 'OR' ? 'OR' : 'AND';
      const conditions = (group.conditions || []).map((cond: any) => convertNode(cond));

      return {
        type: nodeType,
        conditions: conditions
      };
    };

    const rootNode = convertNode(conditionGroup);
    return JSON.stringify(rootNode);
  }

  /**
   * Convert backend conditionTreeJson to frontend conditionGroup
   */
  private treeJsonToConditionGroup(treeJson: string): any {
    if (!treeJson) {
      return { join: 'and', conditions: [] };
    }

    try {
      const tree = JSON.parse(treeJson);
      const convertNode = (node: any): any => {
        if (node.type === 'CONDITION' && node.condition) {
          return {
            field: node.condition.field,
            operator: node.condition.operator,
            value: node.condition.value
          };
        }

        if (node.conditions && Array.isArray(node.conditions)) {
          const join = node.type === 'OR' ? 'or' : 'and';
          return {
            join: join,
            conditions: node.conditions.map((child: any) => convertNode(child))
          };
        }

        return { join: 'and', conditions: [] };
      };

      return convertNode(tree);
    } catch (e) {
      console.error('Failed to parse conditionTreeJson:', e);
      return { join: 'and', conditions: [] };
    }
  }

  // Dashboard Statistics
  async getDashboardStats(params?: {
    storeId?: string;
    from?: string;
    to?: string;
  }) {
    if (this.isDemoUser()) {
      // Return mock dashboard stats for demo
      return this.getMockDashboardStats(params);
    }

    const queryParams = new URLSearchParams();
    if (params?.storeId) queryParams.append('storeId', params.storeId);
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/dashboard/stats${queryString ? '?' + queryString : ''}`;
    // Pass null/undefined as second param to prevent APIClient from adding duplicate query params
    return this.apiClient.get(url, null);
  }

  // Authentication
  async login(credentials: { username: string; password: string }) {
    // Always try real login first, fallback to demo
    try {
      const url = `${API_BASE_URL}/auth/login`;
      return await this.apiClient.create(url, credentials);
    } catch (error) {
      // If real login fails and it's demo credentials, allow demo login
      if (credentials.username === 'demo' || (credentials as any).email === 'demo@marttech.io') {
        return {
          success: true,
          token: 'demo-token-' + Date.now(),
          user: {
            id: 'demo-user',
            username: 'demo',
            email: 'demo@marttech.io',
            role: 'ROLE_COMPANY_ADMIN',
            companyId: 'demo-company',
            storeIds: ['demo-store-1', 'demo-store-2']
          }
        };
      }
      throw error;
    }
  }

  // Password Management
  async changePassword(oldPassword: string, newPassword: string) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true, message: 'Password changed successfully (demo mode)' };
    }

    // Get current user to get username
    const currentUserResponse: any = await this.getCurrentUser();
    const currentUser = currentUserResponse?.data || currentUserResponse;
    const username = currentUser?.username || currentUser?.email;
    
    if (!username) {
      throw new Error('Unable to get current user information');
    }

    const url = `${API_BASE_URL}/auth/change-password`;
    const requestBody = {
      username: username,
      oldPassword: oldPassword,
      newPassword: newPassword
    };
    
    return this.apiClient.create(url, requestBody);
  }

  // User Settings API
  async getSettings() {
    if (this.isDemoUser()) {
      // Return default settings for demo
      return {
        emailNotifications: true,
        dateFormat: 'dd.MM.yyyy',
        timezone: 'Europe/Istanbul',
        defaultStoreId: null
      };
    }

    const url = `${API_BASE_URL}/settings`;
    return this.apiClient.get(url, {});
  }

  async saveSettings(settings: {
    emailNotifications?: boolean;
    dateFormat?: string;
    timezone?: string;
    defaultStoreId?: string | null;
  }) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo user settings saved:', settings);
      return { ...settings, success: true };
    }

    const url = `${API_BASE_URL}/settings`;
    return this.apiClient.update(url, settings);
  }

  async refreshToken(refreshToken: string) {
    try {
      const url = `${API_BASE_URL}/auth/refresh`;
      const response: any = await this.apiClient.create(url, { refreshToken });
      return response?.data || response;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      throw error;
    }
  }

  // Profile API
  async getProfile() {
    if (this.isDemoUser()) {
      // Return demo profile
      return {
        userId: 'demo-user-id',
        username: 'admin@demo.com.tr',
        email: 'admin@demo.com.tr',
        firstName: 'Demo',
        lastName: 'Admin',
        image: null,
        roles: ['ROLE_COMPANY_ADMIN']
      };
    }

    const url = `${API_BASE_URL}/profile`;
    return this.apiClient.get(url, {});
  }

  async updateProfile(profileData: {
    username?: string;
    firstName?: string;
    lastName?: string;
    image?: string | null; // Base64 encoded image
  }) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo user profile updated:', profileData);
      return { ...profileData, success: true };
    }

    const url = `${API_BASE_URL}/profile`;
    return this.apiClient.put(url, profileData);
  }

  async getCurrentUser() {
    // Check sessionStorage first to determine if it's a demo user
    const isDemo = this.isDemoUser();
    if (isDemo) {
      console.log('📦 Returning demo user data (demo user detected)');
      return {
        id: 'demo-user-id',
        userId: 'demo-user-id',
        username: 'admin@demo.com.tr',
        email: 'admin@demo.com.tr',
        roles: ['ROLE_COMPANY_ADMIN'],
        role: 'ROLE_COMPANY_ADMIN',
        companyId: 'demo-company-id',
        storeIds: ['demo-store-1', 'demo-store-2'],
        firstName: 'Demo',
        lastName: 'Admin'
      };
    }
    
    // For real users, check sessionStorage for token and ensure it's set in axios
    const authUser = sessionStorage.getItem("authUser");
    if (authUser) {
      try {
        const userData = JSON.parse(authUser);
        const token = userData.token || userData.access_token;
        if (token && !token.startsWith('demo-token-')) {
          // Ensure token is set in axios defaults (for page refresh scenarios)
          axios.defaults.headers.common["Authorization"] = `Bearer ${token.trim()}`;
          console.log('🔐 getCurrentUser - Token found in sessionStorage, setting in axios defaults');
        }
      } catch (e) {
        console.error('❌ getCurrentUser - Error parsing authUser:', e);
      }
    }
    
    // Fetch from backend - the request interceptor will add the token
    console.log('🌐 Fetching real user from /auth/me');
    const url = `${API_BASE_URL}/auth/me`;
    return this.apiClient.get(url, {});
  }

  // User Management API
  async getUsers() {
    if (this.isDemoUser()) {
      // Return mock users for demo
      const { usersList } = await import('../data/users');
      return usersList;
    }

    const url = `${API_BASE_URL}/users`;
    const response: any = await this.apiClient.get(url, {});
    return Array.isArray(response) ? response : (response?.data || []);
  }

  async getUserById(userId: string) {
    if (this.isDemoUser()) {
      // Return mock user for demo
      const { usersList } = await import('../data/users');
      return usersList.find((u: any) => u.id === userId || u.uid === userId) || null;
    }

    const url = `${API_BASE_URL}/users/${userId}`;
    return this.apiClient.get(url, {});
  }

  async createUser(userData: {
    username: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: string;
    companyId?: string;
    storeIds?: string[];
    storeGroupIds?: string[];
    mobile?: string;
  }) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo user created:', userData);
      return { ...userData, id: `demo-user-${Date.now()}` };
    }

    const url = `${API_BASE_URL}/users`;
    return this.apiClient.create(url, userData);
  }

  async updateUser(userId: string, userData: {
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    companyId?: string;
    storeIds?: string[];
    storeGroupIds?: string[];
    mobile?: string;
    active?: boolean;
  }) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo user updated:', userId, userData);
      return { ...userData, id: userId };
    }

    const url = `${API_BASE_URL}/users/${userId}`;
    return this.apiClient.put(url, userData);
  }

  async assignStoresToUser(userId: string, storeIds: string[]) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo stores assigned:', userId, storeIds);
      return { success: true };
    }

    const url = `${API_BASE_URL}/users/${userId}/stores`;
    return this.apiClient.put(url, { storeIds });
  }

  async assignStoreGroupsToUser(userId: string, storeGroupIds: string[]) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo store groups assigned:', userId, storeGroupIds);
      return { success: true };
    }

    const url = `${API_BASE_URL}/users/${userId}/store-groups`;
    return this.apiClient.put(url, { storeGroupIds });
  }

  async deactivateUser(userId: string) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      console.log('🔧 Demo user deactivated:', userId);
      return { success: true };
    }

    const url = `${API_BASE_URL}/users/${userId}`;
    return this.apiClient.delete(url, {});
  }

  // Notifications API
  async getNotifications(params?: {
    page?: number;
    size?: number;
  }) {
    if (this.isDemoUser()) {
      // Return mock notifications for demo
      return this.getMockNotifications(params);
    }

    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());
    if (params?.size !== undefined) queryParams.append('size', params.size.toString());

    const url = `${API_BASE_URL}/notifications${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response: any = await this.apiClient.get(url, {});
    
    // Backend returns Page<NotificationDTO>
    const pageData = response?.data || response;
    const notifications = pageData?.content || [];
    
    return {
      content: notifications,
      totalElements: pageData?.totalElements || 0,
      totalPages: pageData?.totalPages || 0,
      size: pageData?.size || 20,
      number: pageData?.number || 0
    };
  }

  async getUnreadNotificationCount() {
    if (this.isDemoUser()) {
      // Return mock count for demo
      return { count: 3 };
    }

    const url = `${API_BASE_URL}/notifications/unread-count`;
    const response: any = await this.apiClient.get(url, {});
    return response?.data || response;
  }

  async markNotificationAsRead(notificationId: string) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true };
    }

    const url = `${API_BASE_URL}/notifications/${notificationId}/read`;
    return this.apiClient.put(url, {});
  }

  async markAllNotificationsAsRead() {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true };
    }

    const url = `${API_BASE_URL}/notifications/read-all`;
    return this.apiClient.put(url, {});
  }

  async deleteNotification(notificationId: string) {
    if (this.isDemoUser()) {
      // Simulate success for demo
      return { success: true };
    }

    const url = `${API_BASE_URL}/notifications/${notificationId}`;
    return this.apiClient.delete(url, {});
  }

  // ========== MOCK DATA METHODS ==========
  
  private async getMockTransactions(params?: any) {
    // Import and use existing mock data
    const { generateFakeTransactionsWithAlerts } = await import('../data/transactions');
    const { transactions } = generateFakeTransactionsWithAlerts(1500);
    
    // Apply pagination
    const page = params?.page || 0;
    const size = params?.size || 20;
    const start = page * size;
    const end = start + size;
    
    return {
      content: transactions.slice(start, end),
      totalElements: transactions.length,
      totalPages: Math.ceil(transactions.length / size),
      size: size,
      number: page
    };
  }

  private async getMockFilteredTransactions(params?: any) {
    return this.getMockTransactions(params);
  }

  private async getMockAlerts(params?: any) {
    const { generateFakeTransactionsWithAlerts } = await import('../data/transactions');
    const { alerts } = generateFakeTransactionsWithAlerts(1500);
    
    // Apply pagination
    const page = params?.page || 0;
    const size = params?.size || 20;
    const start = page * size;
    const end = start + size;
    
    return {
      content: alerts.slice(start, end),
      totalElements: alerts.length,
      totalPages: Math.ceil(alerts.length / size),
      size: size,
      number: page
    };
  }

  private async getMockStores() {
    const { mockStores } = await import('../data/stores');
    return mockStores;
  }

  private async getMockStoreGroups() {
    return [
      { id: 'group-1', name: 'Istanbul Bölgesi', storeCount: 8 },
      { id: 'group-2', name: 'Ankara Bölgesi', storeCount: 5 },
      { id: 'group-3', name: 'İzmir Bölgesi', storeCount: 4 }
    ];
  }

  private async getMockScenarios() {
    const { mockScenarios } = await import('../data/scenarios');
    return mockScenarios;
  }

  private async getMockRules(params?: any) {
    const { mockRules } = await import('../data/rules');
    return mockRules;
  }

  // Activity Logs
  async getActivityLogs() {
    if (this.isDemoUser()) {
      // Return mock activity logs for demo
      const { generateFakeActivityLogs } = await import('../data/logs');
      return generateFakeActivityLogs(100);
    }

    const url = `${API_BASE_URL}/activity-logs`;
    const response: any = await this.apiClient.get(url, {});
    const logs = response?.data || response;
    
    // Map backend ActivityLogDTO to frontend format
    return logs.map((log: any) => ({
      id: log.id,
      date: log.timestamp, // Backend sends LocalDateTime as ISO string
      timestamp: new Date(log.timestamp).getTime(),
      userId: log.userId,
      userName: log.userName,
      role: 'N/A', // Backend doesn't provide role, could fetch from user if needed
      action: log.activityType.toLowerCase(), // Convert "LOGIN" to "login" for translation key
      details: `${log.activityType} by ${log.userName}`
    }));
  }

  private async getMockDashboardStats(params?: any) {
    return {
      totalTransactions: 1500,
      todayTransactions: 45,
      thisWeekTransactions: 320,
      thisMonthTransactions: 1500,
      totalRevenue: 75000.00,
      todayRevenue: 2250.00,
      thisWeekRevenue: 16000.00,
      thisMonthRevenue: 75000.00,
      totalAlerts: 150,
      openAlerts: 45,
      inReviewAlerts: 30,
      closedAlerts: 75,
      highSeverityAlerts: 25,
      mediumSeverityAlerts: 60,
      lowSeverityAlerts: 65,
      transactionsByType: {
        SALE: 1200,
        RETURN: 250,
        VOID: 50
      },
      transactionsByPaymentType: {
        CASH: 600,
        CREDIT_CARD: 525,
        DEBIT_CARD: 300,
        MOBILE_PAYMENT: 75
      },
      alertsByScenario: {
        'High Value Return': 60,
        'Suspicious Pattern': 45,
        'Employee Fraud': 30,
        'Customer Fraud': 15
      },
      alertsByRule: {
        'High Value Item Alert': 40,
        'Frequent Returns': 35,
        'Suspicious Timing': 25,
        'Employee Discount Abuse': 20,
        'Customer Refund Pattern': 30
      },
      totalStores: 25,
      activeStores: 23,
      transactionsByStore: {
        'Store 001': 200,
        'Store 002': 180,
        'Store 003': 160,
        'Store 004': 140,
        'Store 005': 120
      },
      alertsByStore: {
        'Store 001': 30,
        'Store 002': 25,
        'Store 003': 20,
        'Store 004': 15,
        'Store 005': 10
      },
      dailyTransactionTrend: {
        '2025-08-19': 110,
        '2025-08-20': 120,
        '2025-08-21': 130,
        '2025-08-22': 140,
        '2025-08-23': 150,
        '2025-08-24': 160,
        '2025-08-25': 170
      },
      dailyAlertTrend: {
        '2025-08-19': 17,
        '2025-08-20': 19,
        '2025-08-21': 21,
        '2025-08-22': 23,
        '2025-08-23': 25,
        '2025-08-24': 27,
        '2025-08-25': 29
      },
      dailyRevenueTrend: {
        '2025-08-19': 5100.00,
        '2025-08-20': 5200.00,
        '2025-08-21': 5300.00,
        '2025-08-22': 5400.00,
        '2025-08-23': 5500.00,
        '2025-08-24': 5600.00,
        '2025-08-25': 5700.00
      }
    };
  }

  private async getMockNotifications(params?: any) {
    const mockNotifications = [
      {
        id: 'notif-1',
        type: 'ALERT',
        title: 'New Alert: High Value Return',
        message: 'A suspicious high value return transaction detected',
        status: 'UNREAD',
        alertId: 'alert-1',
        createdOn: new Date().toISOString()
      },
      {
        id: 'notif-2',
        type: 'ALERT',
        title: 'New Alert: Suspicious Pattern',
        message: 'Multiple returns detected in short time period',
        status: 'UNREAD',
        alertId: 'alert-2',
        createdOn: new Date(Date.now() - 3600000).toISOString()
      },
      {
        id: 'notif-3',
        type: 'SYSTEM',
        title: 'System Update',
        message: 'New features available in dashboard',
        status: 'READ',
        createdOn: new Date(Date.now() - 7200000).toISOString()
      }
    ];
    
    const page = params?.page || 0;
    const size = params?.size || 20;
    const start = page * size;
    const end = start + size;
    
    return {
      content: mockNotifications.slice(start, end),
      totalElements: mockNotifications.length,
      totalPages: Math.ceil(mockNotifications.length / size),
      size: size,
      number: page
    };
  }
}

export const dashboardAPI = new DashboardAPI();
